import ChevronCarouselLeftIcon from '@kijiji/icons/src/icons/ChevronCarouselLeft'
import ChevronCarouselRightIcon from '@kijiji/icons/src/icons/ChevronCarouselRight'
import { type FieldProps, type FormikProps, Field } from 'formik'
import { useTranslation } from 'next-i18next'
import { useCallback } from 'react'
import { useTheme } from 'styled-components'

import { TRANSLATION_KEYS } from '@/constants/localization'
import {
  DEFAULT_TOTAL_DATES,
  DEFAULT_VISIBLE_DATES_PER_PAGE,
  useDatePickerCarrouselDates,
} from '@/features/listing/components/request-viewing/RequestViewForm/useDatePickerCarrouselDates'
import {
  DateOptionLabel,
  HiddenCheckbox,
} from '@/features/listing/components/request-viewing/styled'
import { BodyText } from '@/ui/atoms/body-text'
import { Flex } from '@/ui/atoms/flex'
import { HeadlineText } from '@/ui/atoms/headline-text'
import { IconButton } from '@/ui/atoms/icon-button'
import { IconButtonSize } from '@/ui/atoms/icon-button/IconButton'
import { Spacing } from '@/ui/atoms/spacing'

const DEFAULT_MAX_SELECTED_DATES = 3
const FIELD_NAME = 'preferredDates'

type DatePickerFieldProps = {
  /**
   * Maximum number of dates that can be selected (default: 3)
   */
  maxSelectedDates?: number
  /**
   * Total number of dates to generate in the carousel (default: 30)
   */
  datesToGenerate?: number
  /**
   * Number of dates visible per page in the carousel (default: 4)
   */
  datesPerPage?: number
  /**
   * Name of the form field (default: 'preferredDates')
   */
  name: string
}

/**
 * A form field component that displays a carousel of dates for selection. This component is meant to be used
 * with the `Formik` library. Hence, it must be used within a `Formik` context and a `Form`.
 *
 * @returns The rendered date picker field component
 */
export const DatePickerField = ({
  maxSelectedDates = DEFAULT_MAX_SELECTED_DATES,
  datesToGenerate = DEFAULT_TOTAL_DATES,
  datesPerPage = DEFAULT_VISIBLE_DATES_PER_PAGE,
  name: fieldName = FIELD_NAME,
}: DatePickerFieldProps) => {
  const { colors, spacing } = useTheme()
  const { dates, totalDates, handlePreviousDates, handleNextDates, datesCarrouselStartIndex } =
    useDatePickerCarrouselDates({
      datesToGenerate,
      datesPerPage,
    })
  const { t } = useTranslation(TRANSLATION_KEYS.REQUEST_VIEWING)

  const handleOnChange = useCallback(
    (date: string, form: FormikProps<Record<string, string[]>>) => {
      const preferredDates = form.values[fieldName] || []
      const isDateSelected = preferredDates.includes(date)

      // If date is already selected, we remove it from the array
      if (isDateSelected) {
        const updatedDates = preferredDates.filter((selectedDate) => selectedDate !== date)
        return form.setFieldValue(fieldName, updatedDates)
      }

      // If max dates are selected, we prevent selecting more dates
      if (preferredDates.length >= maxSelectedDates) {
        return
      }

      // Otherwise just add the new date to the array
      const updatedDates = [...preferredDates, date]
      form.setFieldValue(fieldName, updatedDates)
    },
    [maxSelectedDates, fieldName]
  )

  return (
    <fieldset>
      <Flex flexDirection="row" flexGrow={1}>
        <Flex flexDirection="column" flexGrow={1}>
          <HeadlineText as="legend" size="medium">
            {t('request_viewing:form.preferredDatesField.title')}
          </HeadlineText>
          <BodyText size="small" color={colors.grey.light1}>
            {t('request_viewing:form.preferredDatesField.description')}
          </BodyText>
        </Flex>

        <Flex flexDirection="row" gap={spacing.defaultSmall}>
          <IconButton
            size={IconButtonSize.small}
            icon={<ChevronCarouselLeftIcon size="xsm" />}
            onClick={handlePreviousDates}
            isDisabled={datesCarrouselStartIndex === 0}
            aria-label={t('request_viewing:form.preferredDatesField.previousDates')}
            aria-controls="date-picker-carousel"
          />

          <IconButton
            size={IconButtonSize.small}
            icon={<ChevronCarouselRightIcon size="xsm" />}
            onClick={handleNextDates}
            isDisabled={datesCarrouselStartIndex >= totalDates.length - datesPerPage}
            aria-label={t('request_viewing:form.preferredDatesField.nextDates')}
            aria-controls="date-picker-carousel"
          />
        </Flex>
      </Flex>

      <Spacing mTop={spacing.default}>
        <Flex
          id="date-picker-carousel"
          gap={spacing.defaultSmall}
          aria-label={t('request_viewing:form.preferredDatesField.groupLabel')}
          aria-live="polite"
          aria-atomic="true"
        >
          {dates.map(({ date, weekday, monthAndDay }) => {
            return (
              <Field key={date} name={fieldName} type="checkbox" value={date}>
                {({ field: { onChange, ...field }, form }: FieldProps) => {
                  const preferredDates = form.values[fieldName] || []
                  const isMaxDatesSelected = preferredDates.length >= maxSelectedDates
                  const isSelected = !!field.checked
                  const isDisabled = !isSelected && isMaxDatesSelected

                  const getTextColor = (type: 'weekday' | 'monthAndDay') => {
                    if (isDisabled) return colors.grey.light3
                    if (field.checked) return colors.purple.primary
                    return type === 'weekday' ? colors.grey.light1 : colors.grey.primary
                  }

                  return (
                    <DateOptionLabel
                      key={date}
                      isSelected={isSelected}
                      isDisabled={isDisabled}
                      htmlFor={date}
                    >
                      <HiddenCheckbox
                        id={date}
                        onChange={() => handleOnChange(date, form)}
                        aria-label={`${weekday} ${monthAndDay}`}
                        disabled={isDisabled}
                        {...field}
                      />

                      <BodyText size="small" color={getTextColor('weekday')} aria-hidden="true">
                        {weekday}
                      </BodyText>
                      <BodyText
                        size="small"
                        weight={field.checked ? 'medium' : 'regular'}
                        color={getTextColor('monthAndDay')}
                        aria-hidden="true"
                      >
                        {monthAndDay}
                      </BodyText>
                    </DateOptionLabel>
                  )
                }}
              </Field>
            )
          })}
        </Flex>
      </Spacing>
    </fieldset>
  )
}
